import { messageMap } from 'services/messageMap';
import { setBotTyping } from 'store/app/app.actions';
import { MiddlewareStore } from 'store/createStore';
import { newMessageAction } from 'store/messages/messages.actions';
import { IChatMessage } from 'store/messages/messages.state';
import { onMessage } from './onMessage';

const getConfig = (store: MiddlewareStore) => {
    const defaults = {
        enabled: false,
        maxTypingTimeSeconds: 4,
        minTypingTimeSeconds: 1,
        wordsPerMinute: 100,
    };

    const config = store.getState().app.flowTypingIndicatorConfig;

    return {
        ...defaults,
        ...config,
    };
};

export interface IMessageQueuerResponse{ 
    queue: (message: connect.ChatMessageEvent) => void; 
}

export const messageQueuer = (store: MiddlewareStore, session: connect.CustomerChatSession):IMessageQueuerResponse => {
    const messages: connect.ChatMessageEvent[] = [];
    let isRunning = false;

    const getMsgTypingDuration = (message: IChatMessage): number => {
        const config = getConfig(store);

        const wordsPerMinute = config.wordsPerMinute;
        const minDurationMs = config.minTypingTimeSeconds * 1000;
        const maxDurationMs = config.maxTypingTimeSeconds * 1000;

        if (typeof message.content === 'string') {
            const wordCount = message.content.split(' ').length;
            const timeInSeconds = (60 / wordsPerMinute) * wordCount;
            const timeInMilli = timeInSeconds * 1000;

            if (timeInMilli < minDurationMs) return minDurationMs;
            if (timeInMilli > maxDurationMs) return maxDurationMs;
            return timeInMilli;
        }

        return maxDurationMs;
    };

    const displayMessage = (message: IChatMessage) =>
        new Promise(resolve => {
            const { enabled } = getConfig(store);

            const displayMap = messageMap();
            const displayAs = displayMap.get(message.id);
            message.content = displayAs ?? message.content;
            if(!message.isTranslated) message.originalMessage = message.content;

            if (message.discarded) {
                return resolve(message);
            }

            if (message.role !== 'SYSTEM' || !enabled) {
                store.dispatch(newMessageAction(message));
                return resolve(message);
            }

            store.dispatch(setBotTyping());

            setTimeout(() => {
                store.dispatch(newMessageAction(message));
                resolve(message);
            }, getMsgTypingDuration(message));
        });

    const newMessage = async (messageEvent: connect.ChatMessageEvent) => {
        const messages = await onMessage(store, session, messageEvent);

        for (const message of messages) {
            await displayMessage(message);
        }
    };

    const run = async () => {
        isRunning = true;

        while (!!messages.length) {
            await newMessage(messages.shift()!);
        }

        isRunning = false;
    };

    const queue = (message: connect.ChatMessageEvent) => {
        messages.push(message);
        if (!isRunning) run();
    };

    return { queue };
};

export type MessageQueuer = ReturnType<typeof messageQueuer>;
